import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  ArrowUpDownIcon,
  LayoutTable01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { type ReactNode, useMemo, useState } from 'react';
import { Icon } from '@/components/primitives/icon';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableFrame,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  type LoadMore,
  TablePager,
  usePaging,
} from '@/components/ui/table-pager';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  type SortDirection,
  type SortKey,
  sortKey,
  sortRows,
} from '@/lib/sort-rows';
import { readStorage, STORAGE_KEYS, writeStorage } from '@/lib/storage';
import { cn } from '@/lib/utils';

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData, TValue> {
    /**
     * What this column sorts by, for a column whose cell is built by hand
     * (a display column). A column with an accessor sorts by its value; one
     * with neither is not sortable.
     */
    sortValue?: (row: TData) => unknown;
  }
}

interface Sort {
  id: string;
  direction: SortDirection;
}

// biome-ignore lint/suspicious/noExplicitAny: TanStack's own column type takes any value
type AnyColumn<Row> = ColumnDef<Row, any>;

function columnId<Row>(column: AnyColumn<Row>): string {
  if (column.id) return column.id;
  return 'accessorKey' in column ? String(column.accessorKey) : '';
}

/** How a column's rows are put in order, or null when it cannot be sorted. */
function sorter<Row>(column: AnyColumn<Row>): ((row: Row) => SortKey) | null {
  if (column.enableSorting === false) return null;
  const custom = column.meta?.sortValue;
  if (custom) return (row) => sortKey(custom(row));
  if ('accessorFn' in column && column.accessorFn) {
    const read = column.accessorFn;
    return (row) => sortKey(read(row, 0));
  }
  if ('accessorKey' in column && column.accessorKey) {
    const key = String(column.accessorKey);
    return (row) => sortKey((row as Record<string, unknown>)[key]);
  }
  return null;
}

/** The caption a cell gets on a phone: its column's header when that is text. */
function headerLabel(header: unknown): string {
  return typeof header === 'string' ? header : '';
}

function ariaSort(
  direction: SortDirection | null,
  sortable: unknown,
): 'ascending' | 'descending' | 'none' | undefined {
  if (!sortable) return undefined;
  if (!direction) return 'none';
  return direction === 'asc' ? 'ascending' : 'descending';
}

/** One empty list for every table, so its identity never changes. */
const NO_ROWS: never[] = [];

interface DataTableProps<Row> {
  // biome-ignore lint/suspicious/noExplicitAny: TanStack's own column type takes any value
  columns: ColumnDef<Row, any>[];
  data: Row[] | undefined;
  /** Shown in one row when the list loaded empty. */
  empty?: ReactNode;
  /** Rows per page to start with; the reader can change it. */
  pageSize?: number;
  /** A keyset list with more rows on the server: Next loads them. */
  more?: LoadMore;
  /** Names the table for its remembered density; leave out to share one. */
  id?: string;
  /** What the table lists, read out by screen readers; defaults to a phrase made from `id`. */
  caption?: string;
  /** Makes each row open something; the row's own button stays the keyboard way in. */
  onRowClick?: (row: Row, rowEl: HTMLElement) => void;
  /** What opening a row does, in words ("Open transaction 9f2a"); a row it returns undefined for is not clickable. */
  rowLabel?: (row: Row) => string | undefined;
}

/** Controls inside a row that do their own thing when pressed. */
const ROW_CONTROL_SELECTOR =
  'a, button, input, select, textarea, label, [role=checkbox], [role=button], [data-no-row-click]';

type Density = 'comfortable' | 'compact';

/** A row's identity: its `id` when it has one, so rows keep their state across pages and refreshes. */
function rowId(row: unknown, index: number): string {
  const id = (row as { id?: unknown } | null)?.id;
  return typeof id === 'string' || typeof id === 'number'
    ? String(id)
    : String(index);
}

function useDensity(id: string): [Density, (next: Density) => void] {
  const [density, setDensity] = useState<Density>(() =>
    readStorage(STORAGE_KEYS.tableDensity(id)) === 'compact'
      ? 'compact'
      : 'comfortable',
  );
  return [
    density,
    (next) => {
      setDensity(next);
      writeStorage(STORAGE_KEYS.tableDensity(id), next);
    },
  ];
}

/**
 * Every list in the portal. Paging and filters live on the server and in
 * the URL, so the table only lays out the page it is given.
 */
export function DataTable<Row>({
  columns,
  data,
  empty,
  pageSize,
  more,
  id = 'table',
  caption,
  onRowClick,
  rowLabel,
}: Readonly<DataTableProps<Row>>) {
  // TanStack Table v8 keeps one table object and mutates it, which the React
  // Compiler's memoisation does not expect: with it on, opening a dialog on
  // the staff page froze the tab in production builds. Opt this one
  // component out (the compiler's documented escape hatch).
  'use no memo';
  const [sort, setSort] = useState<Sort | null>(null);
  const [density, setDensity] = useDensity(id);
  const compact = density === 'compact';
  const sorters = useMemo(
    () => new Map(columns.map((column) => [columnId(column), sorter(column)])),
    [columns],
  );
  const sorted = useMemo(() => {
    const key = sort ? sorters.get(sort.id) : null;
    return data && sort && key ? sortRows(data, key, sort.direction) : data;
  }, [data, sort, sorters]);
  const paging = usePaging(sorted, pageSize, more?.key);
  const sortBy = (id: string, direction: SortDirection | null) => {
    setSort(direction ? { id, direction } : null);
    paging.goTo(1);
  };
  const table = useReactTable({
    // A new [] on every render while a page loads made the table reset its
    // state on every render, and the tab froze (TanStack Table's auto-reset).
    data: paging.visible ?? NO_ROWS,
    columns,
    getRowId: rowId,
    getCoreRowModel: getCoreRowModel(),
  });

  const choices = columns.flatMap((column) => {
    const id = columnId(column);
    const label = headerLabel(column.header);
    return label && sorters.get(id) ? [{ id, label }] : [];
  });

  return (
    <TableFrame>
      {choices.length > 0 ? (
        <PhoneSort
          choices={choices}
          value={sort}
          onChange={(next) => sortBy(next?.id ?? '', next?.direction ?? null)}
        />
      ) : null}
      <Table containerClassName="md:max-h-[calc(100dvh-15rem)]">
        <caption className="sr-only">
          {caption ?? `${id.replaceAll('-', ' ')} table`}
        </caption>
        <TableHeader>
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id}>
              {group.headers.map((header) => (
                <TableHead
                  key={header.id}
                  aria-sort={ariaSort(
                    sort?.id === header.column.id ? sort.direction : null,
                    sorters.get(header.column.id),
                  )}
                  className="sticky top-0 z-(--z-sticky-header) h-9 bg-surface-2 px-3 text-xs font-medium text-muted-foreground"
                >
                  <SortableHeader
                    label={headerLabel(header.column.columnDef.header)}
                    sortable={Boolean(sorters.get(header.column.id))}
                    direction={
                      sort?.id === header.column.id ? sort.direction : null
                    }
                    onSort={(direction) => sortBy(header.column.id, direction)}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </SortableHeader>
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => {
            const label = rowLabel ? rowLabel(row.original) : 'Open';
            const clickable = Boolean(onRowClick) && label !== undefined;
            const open = (rowEl: HTMLElement) =>
              onRowClick?.(row.original, rowEl);
            return (
              <TableRow
                key={row.id}
                className={cn(
                  clickable
                    ? 'cursor-pointer hover:bg-row-hover focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring'
                    : undefined,
                )}
                tabIndex={clickable ? 0 : undefined}
                aria-label={clickable ? label : undefined}
                onClick={
                  clickable
                    ? (event) => {
                        // A control inside the row does its own thing.
                        const target = event.target as HTMLElement;
                        if (target.closest(ROW_CONTROL_SELECTOR)) return;
                        open(event.currentTarget);
                      }
                    : undefined
                }
                onKeyDown={
                  clickable
                    ? (event) => {
                        if (event.key !== 'Enter' && event.key !== ' ') return;
                        if (event.target !== event.currentTarget) return;
                        event.preventDefault();
                        open(event.currentTarget);
                      }
                    : undefined
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    label={headerLabel(cell.column.columnDef.header)}
                    className={cn('px-3', compact ? 'py-1' : 'py-2.5')}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
          {data?.length === 0 && empty ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="text-muted-foreground"
              >
                {empty}
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
      <TablePager
        paging={paging}
        more={more}
        extra={
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  size="icon-sm"
                  variant="outline"
                  aria-label="Compact rows"
                  aria-pressed={compact}
                  className={cn(
                    'hidden md:inline-flex',
                    compact && 'bg-accent',
                  )}
                  onClick={() =>
                    setDensity(compact ? 'comfortable' : 'compact')
                  }
                />
              }
            >
              <Icon icon={LayoutTable01Icon} size={14} />
            </TooltipTrigger>
            <TooltipContent>
              {compact ? 'Show roomy rows' : 'Show compact rows'}
            </TooltipContent>
          </Tooltip>
        }
      />
    </TableFrame>
  );
}

const SORT_LOOK = {
  none: { icon: ArrowUpDownIcon, state: 'not sorted' },
  asc: { icon: ArrowUp01Icon, state: 'ascending' },
  desc: { icon: ArrowDown01Icon, state: 'descending' },
} as const;

const NEXT: Record<string, SortDirection | null> = {
  none: 'asc',
  asc: 'desc',
  desc: null,
};

/**
 * A column's name, as a button when the column can be sorted: ascending,
 * then descending, then back to the list's own order. The arrows show on
 * hover and keyboard focus, and stay once the column is the one sorted.
 */
function SortableHeader({
  label,
  sortable,
  direction,
  onSort,
  children,
}: Readonly<{
  label: string;
  sortable: boolean;
  direction: SortDirection | null;
  onSort: (direction: SortDirection | null) => void;
  children: ReactNode;
}>) {
  if (!sortable || !label) return <>{children}</>;
  const { icon, state } = SORT_LOOK[direction ?? 'none'];
  return (
    <button
      type="button"
      className="group -mx-1 inline-flex items-center gap-1 rounded-xs px-1 text-left hover:text-foreground"
      aria-label={`Sort by ${label}, currently ${state}`}
      onClick={() => onSort(NEXT[direction ?? 'none'] ?? null)}
    >
      {children}
      <HugeiconsIcon
        icon={icon}
        size={14}
        className={
          direction
            ? 'text-foreground'
            : 'opacity-0 transition-opacity group-hover:opacity-60 group-focus-visible:opacity-60'
        }
      />
    </button>
  );
}

/** On a phone the header row is hidden, so sorting is a choice above the cards. */
function PhoneSort({
  choices,
  value,
  onChange,
}: Readonly<{
  choices: { id: string; label: string }[];
  value: Sort | null;
  onChange: (next: Sort | null) => void;
}>) {
  return (
    <Select
      label="Sort by"
      className="mb-3 w-full md:hidden"
      value={value ? `${value.id}|${value.direction}` : ''}
      onChange={(event) => {
        const [id, direction] = event.target.value.split('|');
        onChange(id ? { id, direction: direction as SortDirection } : null);
      }}
    >
      <option value="">Default order</option>
      {choices.flatMap((choice) => [
        <option key={`${choice.id}|asc`} value={`${choice.id}|asc`}>
          {choice.label}, low to high
        </option>,
        <option key={`${choice.id}|desc`} value={`${choice.id}|desc`}>
          {choice.label}, high to low
        </option>,
      ])}
    </Select>
  );
}
